import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getDataSources,
  getFieldsForDataSource,
  createReport,
  updateReport,
  getReportById,
  previewQuery
} from '../../../api/reports';
import './ReportBuilder.css';

function ReportBuilder() {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!reportId;

  // Report metadata
  const [reportName, setReportName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [dataSource, setDataSource] = useState('');
  const [reportType, setReportType] = useState('TABULAR');
  const [isPublic, setIsPublic] = useState(false);
  const [isTemplate, setIsTemplate] = useState(false);

  // Available options
  const [dataSources, setDataSources] = useState([]);
  const [availableFields, setAvailableFields] = useState([]);

  // Report configuration
  const [selectedFields, setSelectedFields] = useState([]);
  const [filters, setFilters] = useState([]);
  const [joins, setJoins] = useState([]);
  const [groupings, setGroupings] = useState([]);
  const [aggregations, setAggregations] = useState([]);
  const [sortings, setSortings] = useState([]);
  const [displaySettings, setDisplaySettings] = useState({
    title: '',
    showHeader: true,
    showFooter: true,
    alternateRowColors: true,
    fontSize: 'MEDIUM',
    orientation: 'PORTRAIT',
    paperSize: 'A4',
    showGridLines: true
  });
  const [maxRows, setMaxRows] = useState(1000);
  const [showTotals, setShowTotals] = useState(false);

  // UI state
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('fields'); // fields, filters, grouping, sorting, display, query

  // SQL Query Preview state
  const [sqlQuery, setSqlQuery] = useState('');
  const [loadingQuery, setLoadingQuery] = useState(false);

  // Advanced SQL Mode state
  const [useAdvancedSql, setUseAdvancedSql] = useState(false);
  const [customSqlQuery, setCustomSqlQuery] = useState('');

  useEffect(() => {
    const initializeData = async () => {
      // Load data sources first
      await loadDataSources();
      // Then load report if in edit mode
      if (isEditMode && reportId) {
        await loadReport();
      }
    };
    initializeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (dataSource) {
      loadFields();
    }
  }, [dataSource]);

  const loadDataSources = async () => {
    try {
      const sources = await getDataSources();
      setDataSources(sources);
    } catch (err) {
      setError('Failed to load data sources. Please try again.');
    }
  };

  const loadFields = async () => {
    setLoading(true);
    try {
      const fields = await getFieldsForDataSource(dataSource);
      setAvailableFields(fields);
    } catch (err) {
      setError('Failed to load fields. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadReport = async () => {
    setLoading(true);
    setError(null); // Clear any previous errors
    try {
      const response = await getReportById(reportId);

      // Extract the actual report data from the response
      const report = response.report || response;

      setReportName(report.reportName);
      setDescription(report.description || '');
      setCategory(report.category || '');
      setDataSource(report.dataSource);
      setReportType(report.reportType);
      setIsPublic(report.isPublic);
      setIsTemplate(report.isTemplate);

      // Parse configuration - handle null/undefined configuration
      const config = report.configuration
        ? (typeof report.configuration === 'string'
          ? JSON.parse(report.configuration)
          : report.configuration)
        : {};


      setSelectedFields(config.fields || []);
      setFilters(config.filters || []);
      setJoins(config.joins || []);
      setGroupings(config.groupings || []);
      setAggregations(config.aggregations || []);
      setSortings(config.sortings || []);
      setDisplaySettings(config.displaySettings || displaySettings);
      setMaxRows(config.maxRows || 1000);
      setShowTotals(config.showTotals || false);
    } catch (err) {
      setError('Failed to load report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Validation
    if (!reportName || !reportName.trim()) {
      alert('Please enter a report name');
      return;
    }
    if (!dataSource) {
      alert('Please select a data source');
      return;
    }
    if (selectedFields.length === 0) {
      alert('Please select at least one field');
      return;
    }

    setSaving(true);
    try {
      const reportData = {
        reportName,
        description,
        category,
        dataSource,
        reportType,
        isPublic,
        isTemplate,
        configuration: {
          fields: selectedFields,
          filters,
          joins,
          groupings,
          aggregations,
          sortings,
          displaySettings: {
            ...displaySettings,
            title: displaySettings.title || reportName
          },
          maxRows,
          showTotals
        }
      };

      if (isEditMode) {
        await updateReport(reportId, reportData);
      } else {
        await createReport(reportData);
      }

      navigate('/admin/reports');
    } catch (err) {
      alert('Failed to save report: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  const loadSqlPreview = async () => {

    if (!dataSource) {
      setSqlQuery('-- Please select a data source first');
      return;
    }

    if (selectedFields.length === 0) {
      setSqlQuery('-- Please select at least one field in the Fields tab before previewing the query');
      return;
    }

    setLoadingQuery(true);
    try {
      const configuration = {
        fields: selectedFields,
        filters,
        joins,
        groupings,
        aggregations,
        sortings,
        displaySettings,
        maxRows,
        showTotals
      };

      const response = await previewQuery(dataSource, configuration, {});
      setSqlQuery(response.query || '');
    } catch (err) {
      setSqlQuery(`-- Error generating query. Please check your configuration.`);
    } finally {
      setLoadingQuery(false);
    }
  };

  const addField = (field) => {
    if (selectedFields.find(f => f.fieldName === field.fieldName)) {
      alert('Field already added');
      return;
    }
    setSelectedFields([...selectedFields, {
      fieldName: field.fieldName,
      displayName: field.displayName || field.fieldName,
      dataType: field.dataType,
      visible: true,
      width: null,
      alignment: 'LEFT',
      format: ''
    }]);
  };

  const removeField = (fieldName) => {
    setSelectedFields(selectedFields.filter(f => f.fieldName !== fieldName));
  };

  const updateField = (fieldName, updates) => {
    setSelectedFields(selectedFields.map(f =>
      f.fieldName === fieldName ? { ...f, ...updates } : f
    ));
  };

  const addFilter = () => {
    setFilters([...filters, {
      fieldName: '',
      operator: 'EQUALS',
      value: '',
      dataType: 'STRING'
    }]);
  };

  const removeFilter = (index) => {
    setFilters(filters.filter((_, i) => i !== index));
  };

  const updateFilter = (index, updates) => {
    setFilters(filters.map((f, i) => i === index ? { ...f, ...updates } : f));
  };

  const addGrouping = () => {
    setGroupings([...groupings, {
      fieldName: '',
      groupOrder: groupings.length,
      showSubtotals: true
    }]);
  };

  const removeGrouping = (index) => {
    setGroupings(groupings.filter((_, i) => i !== index));
  };

  const updateGrouping = (index, updates) => {
    setGroupings(groupings.map((g, i) => i === index ? { ...g, ...updates } : g));
  };

  const addAggregation = () => {
    setAggregations([...aggregations, {
      fieldName: '',
      function: 'COUNT',
      label: ''
    }]);
  };

  const removeAggregation = (index) => {
    setAggregations(aggregations.filter((_, i) => i !== index));
  };

  const updateAggregation = (index, updates) => {
    setAggregations(aggregations.map((a, i) => i === index ? { ...a, ...updates } : a));
  };

  const addSorting = () => {
    setSortings([...sortings, {
      fieldName: '',
      direction: 'ASC',
      sortOrder: sortings.length + 1
    }]);
  };

  const removeSorting = (index) => {
    setSortings(sortings.filter((_, i) => i !== index));
  };

  const updateSorting = (index, updates) => {
    setSortings(sortings.map((s, i) => i === index ? { ...s, ...updates } : s));
  };

  const addJoin = () => {
    setJoins([...joins, {
      joinDataSource: '',
      joinType: 'INNER',
      primaryField: '',
      joinField: '',
      alias: ''
    }]);
  };

  const removeJoin = (index) => {
    setJoins(joins.filter((_, i) => i !== index));
  };

  const updateJoin = (index, updates) => {
    setJoins(joins.map((j, i) => i === index ? { ...j, ...updates } : j));
  };

  if (loading && isEditMode) {
    return <div className="report-builder-container"><div className="loading">Loading report...</div></div>;
  }

  return (
    <div className="report-builder-container">
      <div className="report-builder-header">
        <h1>{isEditMode ? 'Edit Report' : 'Create New Report'}</h1>
        <div className="header-actions">
          <button className="btn-secondary" onClick={() => navigate('/admin/reports')}>Cancel</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Report'}
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Basic Information */}
      <div className="builder-section">
        <h2>Basic Information</h2>
        <div className="form-grid">
          <div className="form-group">
            <label>Report Name *</label>
            <input
              type="text"
              value={reportName}
              onChange={(e) => setReportName(e.target.value)}
              placeholder="Enter report name"
            />
          </div>
          <div className="form-group">
            <label>Category</label>
            <input
              type="text"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="e.g., User Management, Fleet"
            />
          </div>
          <div className="form-group full-width">
            <label>Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what this report does"
              rows={3}
            />
          </div>
          <div className="form-group">
            <label>Data Source *</label>
            <select value={dataSource} onChange={(e) => setDataSource(e.target.value)}>
              <option value="">Select data source</option>
              {dataSources.map(ds => (
                <option key={ds.name} value={ds.name}>
                  {ds.displayName} (Table: {ds.tableName})
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Report Type *</label>
            <select value={reportType} onChange={(e) => setReportType(e.target.value)}>
              <option value="TABULAR">Tabular</option>
              <option value="SUMMARY">Summary</option>
              <option value="MATRIX">Matrix</option>
            </select>
          </div>
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
              />
              Make Public
            </label>
          </div>
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={isTemplate}
                onChange={(e) => setIsTemplate(e.target.checked)}
              />
              Save as Template
            </label>
          </div>
        </div>
      </div>

      {/* Configuration Tabs */}
      {dataSource && (
        <>
          <div className="builder-tabs">
            <button
              className={activeTab === 'fields' ? 'active' : ''}
              onClick={() => setActiveTab('fields')}
            >
              Fields
            </button>
            <button
              className={activeTab === 'filters' ? 'active' : ''}
              onClick={() => setActiveTab('filters')}
            >
              Filters
            </button>
            <button
              className={activeTab === 'joins' ? 'active' : ''}
              onClick={() => setActiveTab('joins')}
            >
              Joins
            </button>
            <button
              className={activeTab === 'grouping' ? 'active' : ''}
              onClick={() => setActiveTab('grouping')}
            >
              Grouping & Aggregation
            </button>
            <button
              className={activeTab === 'sorting' ? 'active' : ''}
              onClick={() => setActiveTab('sorting')}
            >
              Sorting
            </button>
            <button
              className={activeTab === 'display' ? 'active' : ''}
              onClick={() => setActiveTab('display')}
            >
              Display Settings
            </button>
            <button
              className={activeTab === 'query' ? 'active' : ''}
              onClick={() => {
                setActiveTab('query');
                // Delay the preview to ensure state is updated
                setTimeout(() => loadSqlPreview(), 100);
              }}
            >
              Query Preview
            </button>
            <button
              className={activeTab === 'advanced' ? 'active' : ''}
              onClick={() => setActiveTab('advanced')}
            >
              Advanced SQL
            </button>
          </div>

          <div className="builder-content">
            {/* Fields Tab */}
            {activeTab === 'fields' && (
              <div className="fields-section">
                <div className="available-fields">
                  <h3>Available Fields</h3>
                  <div className="field-list">
                    {availableFields.map(field => (
                      <div key={field.fieldName} className="field-item">
                        <div className="field-details">
                          <span className="field-name" title={`Database column: ${field.fieldName}`}>
                            {field.displayName || field.fieldName}
                          </span>
                          <span className="field-db-name">({field.fieldName})</span>
                        </div>
                        <span className="field-type">{field.dataType}</span>
                        <button
                          className="btn-sm"
                          onClick={() => addField(field)}
                          disabled={selectedFields.find(f => f.fieldName === field.fieldName)}
                        >
                          Add
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="selected-fields">
                  <h3>Selected Fields ({selectedFields.length})</h3>
                  {selectedFields.length === 0 ? (
                    <p className="empty-message">No fields selected. Add fields from the left panel.</p>
                  ) : (
                    <div className="selected-field-list">
                      {selectedFields.map((field, index) => (
                        <div key={field.fieldName} className="selected-field-item">
                          <div className="field-info">
                            <span className="field-order">{index + 1}</span>
                            <div className="field-input-group">
                              <input
                                type="text"
                                value={field.displayName}
                                onChange={(e) => updateField(field.fieldName, { displayName: e.target.value })}
                                placeholder="Column label / alias"
                                title="Display name for the column header and SQL alias"
                              />
                              <span className="field-db-hint">({field.fieldName})</span>
                            </div>
                          </div>
                          <div className="field-options">
                            <label>
                              <input
                                type="checkbox"
                                checked={field.visible}
                                onChange={(e) => updateField(field.fieldName, { visible: e.target.checked })}
                              />
                              Visible
                            </label>
                            <select
                              value={field.alignment || 'LEFT'}
                              onChange={(e) => updateField(field.fieldName, { alignment: e.target.value })}
                            >
                              <option value="LEFT">Left</option>
                              <option value="CENTER">Center</option>
                              <option value="RIGHT">Right</option>
                            </select>
                            {(field.dataType === 'DATE' || field.dataType === 'TIMESTAMP' || field.dataType === 'DATETIME') && (
                              <select
                                value={field.format || ''}
                                onChange={(e) => updateField(field.fieldName, { format: e.target.value })}
                                className="format-select"
                              >
                                <option value="">Default format</option>
                                <option value="yyyy-MM-dd">yyyy-MM-dd (2024-12-04)</option>
                                <option value="MM/dd/yyyy">MM/dd/yyyy (12/04/2024)</option>
                                <option value="dd/MM/yyyy">dd/MM/yyyy (04/12/2024)</option>
                                <option value="MMM dd, yyyy">MMM dd, yyyy (Dec 04, 2024)</option>
                                <option value="MMMM dd, yyyy">MMMM dd, yyyy (December 04, 2024)</option>
                                <option value="yyyy-MM-dd HH:mm:ss">yyyy-MM-dd HH:mm:ss (2024-12-04 14:30:00)</option>
                                <option value="dd/MM/yyyy HH:mm">dd/MM/yyyy HH:mm (04/12/2024 14:30)</option>
                                <option value="MMM dd, yyyy HH:mm">MMM dd, yyyy HH:mm (Dec 04, 2024 14:30)</option>
                                <option value="EEE, MMM dd, yyyy">EEE, MMM dd, yyyy (Wed, Dec 04, 2024)</option>
                              </select>
                            )}
                            <button className="btn-danger btn-sm" onClick={() => removeField(field.fieldName)}>
                              Remove
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Filters Tab */}
            {activeTab === 'filters' && (
              <div className="filters-section">
                <div className="section-header">
                  <h3>Filters</h3>
                  <button className="btn-primary btn-sm" onClick={addFilter}>+ Add Filter</button>
                </div>
                {filters.length === 0 ? (
                  <p className="empty-message">No filters defined. Click "Add Filter" to create one.</p>
                ) : (
                  <div className="filter-list">
                    {filters.map((filter, index) => (
                      <div key={index} className="filter-item">
                        <select
                          value={filter.fieldName}
                          onChange={(e) => updateFilter(index, { fieldName: e.target.value })}
                        >
                          <option value="">Select field</option>
                          {availableFields.map(field => (
                            <option key={field.fieldName} value={field.fieldName}>
                              {field.displayName || field.fieldName}
                            </option>
                          ))}
                        </select>
                        <select
                          value={filter.operator}
                          onChange={(e) => updateFilter(index, { operator: e.target.value })}
                        >
                          <option value="EQUALS">Equals</option>
                          <option value="NOT_EQUALS">Not Equals</option>
                          <option value="CONTAINS">Contains</option>
                          <option value="STARTS_WITH">Starts With</option>
                          <option value="ENDS_WITH">Ends With</option>
                          <option value="GREATER_THAN">Greater Than</option>
                          <option value="LESS_THAN">Less Than</option>
                          <option value="BETWEEN">Between</option>
                          <option value="IN">In</option>
                          <option value="IS_NULL">Is Null</option>
                          <option value="IS_NOT_NULL">Is Not Null</option>
                          <option value="TODAY">Today</option>
                          <option value="THIS_WEEK">This Week</option>
                          <option value="THIS_MONTH">This Month</option>
                          <option value="LAST_N_DAYS">Last N Days</option>
                        </select>
                        {!['IS_NULL', 'IS_NOT_NULL', 'TODAY', 'THIS_WEEK', 'THIS_MONTH'].includes(filter.operator) && (
                          <input
                            type="text"
                            value={filter.value}
                            onChange={(e) => updateFilter(index, { value: e.target.value })}
                            placeholder="Value"
                          />
                        )}
                        <button className="btn-danger btn-sm" onClick={() => removeFilter(index)}>Remove</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Joins Tab */}
            {activeTab === 'joins' && (
              <div className="joins-section">
                <div className="section-header">
                  <h3>Table Joins</h3>
                  <button className="btn-primary btn-sm" onClick={addJoin}>+ Add Join</button>
                </div>
                <p className="info-text">Configure joins to combine data from multiple tables. The current data source ({dataSource}) is the primary table.</p>
                {joins.length === 0 ? (
                  <p className="empty-message">No joins defined. Click "Add Join" to join another table.</p>
                ) : (
                  <div className="join-list">
                    {joins.map((join, index) => (
                      <div key={index} className="join-item">
                        <div className="join-row">
                          <div className="form-group">
                            <label>Join Type</label>
                            <select
                              value={join.joinType}
                              onChange={(e) => updateJoin(index, { joinType: e.target.value })}
                            >
                              <option value="INNER">INNER JOIN</option>
                              <option value="LEFT">LEFT JOIN</option>
                              <option value="RIGHT">RIGHT JOIN</option>
                              <option value="FULL">FULL OUTER JOIN</option>
                            </select>
                          </div>
                          <div className="form-group">
                            <label>Table to Join</label>
                            <select
                              value={join.joinDataSource}
                              onChange={(e) => updateJoin(index, { joinDataSource: e.target.value })}
                            >
                              <option value="">Select table</option>
                              {dataSources.filter(ds => ds.name !== dataSource).map(ds => (
                                <option key={ds.name} value={ds.name}>
                                  {ds.displayName} ({ds.tableName})
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="form-group">
                            <label>Table Alias (Optional)</label>
                            <input
                              type="text"
                              value={join.alias || ''}
                              onChange={(e) => updateJoin(index, { alias: e.target.value })}
                              placeholder="e.g., v, b, u"
                            />
                          </div>
                        </div>
                        <div className="join-row">
                          <div className="form-group">
                            <label>Primary Table Field</label>
                            <select
                              value={join.primaryField}
                              onChange={(e) => updateJoin(index, { primaryField: e.target.value })}
                            >
                              <option value="">Select field</option>
                              {availableFields.map(field => (
                                <option key={field.fieldName} value={field.fieldName}>
                                  {field.displayName || field.fieldName}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="form-group">
                            <label>Joined Table Field</label>
                            <input
                              type="text"
                              value={join.joinField}
                              onChange={(e) => updateJoin(index, { joinField: e.target.value })}
                              placeholder="Enter field name from joined table"
                            />
                          </div>
                          <div className="form-group">
                            <button className="btn-danger btn-sm" onClick={() => removeJoin(index)}>Remove Join</button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Grouping & Aggregation Tab */}
            {activeTab === 'grouping' && (
              <div className="grouping-section">
                <div className="section-header">
                  <h3>Grouping</h3>
                  <button className="btn-primary btn-sm" onClick={addGrouping}>+ Add Group</button>
                </div>
                {groupings.length === 0 ? (
                  <p className="empty-message">No grouping defined.</p>
                ) : (
                  <div className="grouping-list">
                    {groupings.map((grouping, index) => (
                      <div key={index} className="grouping-item">
                        <select
                          value={grouping.fieldName}
                          onChange={(e) => updateGrouping(index, { fieldName: e.target.value })}
                        >
                          <option value="">Select field</option>
                          {selectedFields.map(field => (
                            <option key={field.fieldName} value={field.fieldName}>
                              {field.displayName}
                            </option>
                          ))}
                        </select>
                        <label>
                          <input
                            type="checkbox"
                            checked={grouping.showSubtotals}
                            onChange={(e) => updateGrouping(index, { showSubtotals: e.target.checked })}
                          />
                          Show Subtotals
                        </label>
                        <button className="btn-danger btn-sm" onClick={() => removeGrouping(index)}>Remove</button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="section-header" style={{ marginTop: '20px' }}>
                  <h3>Aggregations</h3>
                  <button className="btn-primary btn-sm" onClick={addAggregation}>+ Add Aggregation</button>
                </div>
                {aggregations.length === 0 ? (
                  <p className="empty-message">No aggregations defined.</p>
                ) : (
                  <div className="aggregation-list">
                    {aggregations.map((agg, index) => (
                      <div key={index} className="aggregation-item">
                        <select
                          value={agg.fieldName}
                          onChange={(e) => updateAggregation(index, { fieldName: e.target.value })}
                        >
                          <option value="">Select field</option>
                          {selectedFields.map(field => (
                            <option key={field.fieldName} value={field.fieldName}>
                              {field.displayName}
                            </option>
                          ))}
                        </select>
                        <select
                          value={agg.function}
                          onChange={(e) => updateAggregation(index, { function: e.target.value })}
                        >
                          <option value="COUNT">Count</option>
                          <option value="SUM">Sum</option>
                          <option value="AVG">Average</option>
                          <option value="MIN">Min</option>
                          <option value="MAX">Max</option>
                        </select>
                        <input
                          type="text"
                          value={agg.label}
                          onChange={(e) => updateAggregation(index, { label: e.target.value })}
                          placeholder="Label (e.g., Total Count)"
                        />
                        <button className="btn-danger btn-sm" onClick={() => removeAggregation(index)}>Remove</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Sorting Tab */}
            {activeTab === 'sorting' && (
              <div className="sorting-section">
                <div className="section-header">
                  <h3>Sorting</h3>
                  <button className="btn-primary btn-sm" onClick={addSorting}>+ Add Sort</button>
                </div>
                {sortings.length === 0 ? (
                  <p className="empty-message">No sorting defined.</p>
                ) : (
                  <div className="sorting-list">
                    {sortings.map((sort, index) => (
                      <div key={index} className="sorting-item">
                        <span className="sort-order">Order: {index + 1}</span>
                        <select
                          value={sort.fieldName}
                          onChange={(e) => updateSorting(index, { fieldName: e.target.value })}
                        >
                          <option value="">Select field</option>
                          {selectedFields.map(field => (
                            <option key={field.fieldName} value={field.fieldName}>
                              {field.displayName}
                            </option>
                          ))}
                        </select>
                        <select
                          value={sort.direction}
                          onChange={(e) => updateSorting(index, { direction: e.target.value })}
                        >
                          <option value="ASC">Ascending</option>
                          <option value="DESC">Descending</option>
                        </select>
                        <button className="btn-danger btn-sm" onClick={() => removeSorting(index)}>Remove</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Display Settings Tab */}
            {activeTab === 'display' && (
              <div className="display-section">
                <h3>Display Settings</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Report Title</label>
                    <input
                      type="text"
                      value={displaySettings.title}
                      onChange={(e) => setDisplaySettings({ ...displaySettings, title: e.target.value })}
                      placeholder={reportName}
                    />
                  </div>
                  <div className="form-group">
                    <label>Font Size</label>
                    <select
                      value={displaySettings.fontSize}
                      onChange={(e) => setDisplaySettings({ ...displaySettings, fontSize: e.target.value })}
                    >
                      <option value="SMALL">Small</option>
                      <option value="MEDIUM">Medium</option>
                      <option value="LARGE">Large</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Orientation</label>
                    <select
                      value={displaySettings.orientation}
                      onChange={(e) => setDisplaySettings({ ...displaySettings, orientation: e.target.value })}
                    >
                      <option value="PORTRAIT">Portrait</option>
                      <option value="LANDSCAPE">Landscape</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Paper Size</label>
                    <select
                      value={displaySettings.paperSize}
                      onChange={(e) => setDisplaySettings({ ...displaySettings, paperSize: e.target.value })}
                    >
                      <option value="A4">A4</option>
                      <option value="LETTER">Letter</option>
                      <option value="LEGAL">Legal</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Max Rows</label>
                    <input
                      type="number"
                      value={maxRows}
                      onChange={(e) => setMaxRows(parseInt(e.target.value) || 1000)}
                      min="1"
                      max="100000"
                    />
                  </div>
                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={displaySettings.showHeader}
                        onChange={(e) => setDisplaySettings({ ...displaySettings, showHeader: e.target.checked })}
                      />
                      Show Header
                    </label>
                  </div>
                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={displaySettings.showFooter}
                        onChange={(e) => setDisplaySettings({ ...displaySettings, showFooter: e.target.checked })}
                      />
                      Show Footer
                    </label>
                  </div>
                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={displaySettings.alternateRowColors}
                        onChange={(e) => setDisplaySettings({ ...displaySettings, alternateRowColors: e.target.checked })}
                      />
                      Alternate Row Colors
                    </label>
                  </div>
                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={displaySettings.showGridLines}
                        onChange={(e) => setDisplaySettings({ ...displaySettings, showGridLines: e.target.checked })}
                      />
                      Show Grid Lines
                    </label>
                  </div>
                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={showTotals}
                        onChange={(e) => setShowTotals(e.target.checked)}
                      />
                      Show Totals
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Query Preview Tab */}
            {activeTab === 'query' && (
              <div className="query-preview-tab">
                <div className="query-preview-header">
                  <h3>SQL Query Preview</h3>
                  <button
                    className="btn btn-secondary"
                    onClick={loadSqlPreview}
                    disabled={loadingQuery || !dataSource}
                  >
                    {loadingQuery ? 'Generating...' : 'Refresh Query'}
                  </button>
                </div>
                <div className="sql-query-display">
                  {loadingQuery ? (
                    <div className="loading-indicator">
                      <p>Generating SQL query...</p>
                    </div>
                  ) : (
                    <pre>{sqlQuery || '-- No query generated yet. Please select fields and configure your report.'}</pre>
                  )}
                </div>
                <div className="query-info">
                  <p className="info-text">
                    This is the SQL query that will be executed based on your current configuration.
                    You can use this to verify the query logic before running the report.
                  </p>
                </div>
              </div>
            )}

            {/* Advanced SQL Tab */}
            {activeTab === 'advanced' && (
              <div className="advanced-sql-tab">
                <div className="section-header">
                  <h3>Advanced SQL Mode</h3>
                  <label className="sql-mode-toggle">
                    <input
                      type="checkbox"
                      checked={useAdvancedSql}
                      onChange={(e) => setUseAdvancedSql(e.target.checked)}
                    />
                    Enable Advanced SQL Mode
                  </label>
                </div>

                {!useAdvancedSql ? (
                  <div className="query-info">
                    <p className="info-text">
                      <strong>Advanced SQL Mode</strong> allows you to write custom SQL queries instead of using the visual builder.
                      This gives you complete control over your queries for complex reporting needs.
                      <br /><br />
                      <strong>Important:</strong> Only SELECT queries are allowed for security reasons.
                      Queries containing INSERT, UPDATE, DELETE, DROP, or other destructive operations will be rejected.
                      <br /><br />
                      Enable the toggle above to start writing custom SQL.
                    </p>
                  </div>
                ) : (
                  <div className="sql-editor-section">
                    <div className="query-info" style={{ marginBottom: '15px' }}>
                      <p className="info-text">
                        <strong>Security Note:</strong> Only SELECT and WITH (CTE) queries are permitted.
                        Your query will be validated before execution to prevent SQL injection and destructive operations.
                      </p>
                    </div>

                    <div className="form-group">
                      <label>Custom SQL Query</label>
                      <textarea
                        value={customSqlQuery}
                        onChange={(e) => setCustomSqlQuery(e.target.value)}
                        placeholder="-- Write your SELECT query here
SELECT
  column1,
  column2,
  column3
FROM
  your_table
WHERE
  condition = 'value'
ORDER BY
  column1;"
                        rows={20}
                        style={{
                          fontFamily: "'Courier New', Courier, monospace",
                          fontSize: '14px',
                          padding: '15px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          backgroundColor: '#f8f9fa',
                          resize: 'vertical'
                        }}
                      />
                    </div>

                    <div className="sql-editor-actions">
                      <button
                        className="btn btn-secondary"
                        onClick={() => {
                          // Format the SQL query (basic formatting)
                          const formatted = customSqlQuery
                            .replace(/\bSELECT\b/gi, '\nSELECT\n  ')
                            .replace(/\bFROM\b/gi, '\nFROM\n  ')
                            .replace(/\bWHERE\b/gi, '\nWHERE\n  ')
                            .replace(/\bAND\b/gi, '\n  AND ')
                            .replace(/\bOR\b/gi, '\n  OR ')
                            .replace(/\bJOIN\b/gi, '\nJOIN\n  ')
                            .replace(/\bLEFT JOIN\b/gi, '\nLEFT JOIN\n  ')
                            .replace(/\bRIGHT JOIN\b/gi, '\nRIGHT JOIN\n  ')
                            .replace(/\bINNER JOIN\b/gi, '\nINNER JOIN\n  ')
                            .replace(/\bON\b/gi, '\n  ON ')
                            .replace(/\bGROUP BY\b/gi, '\nGROUP BY\n  ')
                            .replace(/\bHAVING\b/gi, '\nHAVING\n  ')
                            .replace(/\bORDER BY\b/gi, '\nORDER BY\n  ')
                            .replace(/\bLIMIT\b/gi, '\nLIMIT ')
                            .trim();
                          setCustomSqlQuery(formatted);
                        }}
                      >
                        Format SQL
                      </button>
                      <button
                        className="btn btn-secondary"
                        onClick={() => setCustomSqlQuery('')}
                      >
                        Clear
                      </button>
                    </div>

                    <div className="query-info" style={{ marginTop: '15px', backgroundColor: '#fff3cd', borderLeftColor: '#ffc107' }}>
                      <p className="info-text">
                        <strong>Tips:</strong>
                        <br />- Use the visual builder tabs to explore available fields and tables
                        <br />- Query validation happens when you save the report
                        <br />- Use the "Format SQL" button to auto-indent your query
                        <br />- Test your query before saving by running the report in preview mode
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default ReportBuilder;
